import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { getBackgroundColor } from '../../utils';
import { Button } from '../button';
import { Info } from '../icon';
import { Spacer } from '../spacer';
import { Typography } from '../typography';

// Interface

export interface MessageBannerProps {
  heading: string;
  description?: string;
  buttonOne?: ButtonProps;
  buttonTwo?: ButtonProps;
}

export interface ButtonProps {
  url?: string;
  label?: string;
  onClick?: any;
}

// Styles

const StyledMessageBanner = styled.div`
  ${({ theme: { borderRadii, space, media, maxMedia } }) => css`
    border-radius: ${borderRadii.xSmall};
    overflow: hidden;
    ${getBackgroundColor('brandTwo100')}
    padding: ${space.small};
    display: flex;
    gap: ${space.small};

    @media (max-width: ${maxMedia.medium}) {
      flex-direction: column;
    }

    @media (min-width: ${media.medium}) {
      padding: ${space.medium};
      align-items: center;
      justify-content: space-between;
    }
  `};
`;

const StyledMessageBannerWrapper = styled.div`
  ${({ theme: { media, space } }) => css`
    display: flex;
    gap: ${space.xxSmall};

    @media (min-width: ${media.medium}) {
      gap: ${space.small};
    }
  `};
`;

const StyledButtons = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    gap: ${space.xSmall};
    flex-shrink: 0;

    @media (max-width: ${maxMedia.medium}) {
      flex-direction: column;

      > * {
        width: 100%;
      }
    }
  `};
`;

// JSX

export const MessageBanner: FC<MessageBannerProps> = ({
  heading,
  description,
  buttonOne,
  buttonTwo,
}) => (
  <StyledMessageBanner>
    <StyledMessageBannerWrapper>
      <Info size="large" />
      <Spacer spacing="xSmall">
        {heading && (
          <Typography component="h3" variant="headingMedium">
            {heading}
          </Typography>
        )}
        {description && (
          <Typography component="p" variant="bodyMedium">
            {description}
          </Typography>
        )}
      </Spacer>
    </StyledMessageBannerWrapper>
    {buttonOne || buttonTwo ? (
      <StyledButtons>
        {buttonOne && <Button variant="primary" arrowRight {...buttonOne} />}
        {buttonTwo && (
          <Button variant="secondaryInverseOutline" arrowRight {...buttonTwo} />
        )}
      </StyledButtons>
    ) : (
      ''
    )}
  </StyledMessageBanner>
);

export default MessageBanner;
